import {
  DashboardBrainPart,
  DashboardBrainPartMriLabel,
  DashboardBrainPartMriLabelAtypicalCondition,
  DashboardBrainPartSecondaryLevel,
  DashboardBrainPartsFlatListMap,
  DashboardBrainPartsParentMap,
  DashboardMriLabelColorType,
} from './dashboard-brain-parts-general.symbols';
import {
  DashboardBrainPartCalculatedData,
  DashboardChartPvsDataRelation,
  DashboardChartWhiteMatterDataRelation,
} from '../../dashboard-chart-data/symbols/dashboard-chart-data-general.symbols';
import {
  DashboardChartDataUtilsComparisonResult,
  firstTypicalZoneAgePercentagePoint,
  secondTypicalZoneAgePercentagePoint,
} from '../../dashboard-chart-data/symbols/dashboard-chart-data-compare-percentage-utils.symbols';
import {SubmissionScanImageType} from '../../dashboard/symbols/general.symbols';
import {dashboardChartDataDefineWmScoreGroup} from '../../dashboard-chart-data/symbols/dashboard-chart-data-wm-utils.symbols';
import {
  dashboardChartDataDefinePvsScoreGroup,
  DashboardChartDataPvsGroupColors,
} from '../../dashboard-chart-data/symbols/dashboard-chart-data-pvs-utils.symbols';
import {HexColor} from '../../shared/symbols';

/**
 * Creates a parent map for the dashboard brain parts.
 * The result is an array of relations between the brain part id and its parent id.
 */
export const dashboardBrainPartsCreateParentMap = (brainParts: DashboardBrainPartSecondaryLevel[]): DashboardBrainPartsParentMap => {
  // Initialize stack with the root brain parts.
  const stackToProceed = [...brainParts];

  // Initialize a parent map as an JS Map.
  const parentMap: Map<DashboardBrainPartSecondaryLevel['id'], DashboardBrainPartSecondaryLevel['id']> = new Map();

  // Iterate over the stack.
  while (stackToProceed.length > 0) {
    // Extract the last element from the stack.
    const brainPart = stackToProceed.pop();

    // Push the child regions of the extracted element to the stack and add them to the parent map.
    for (const child of brainPart.childRegions) {
      stackToProceed.push(child);
      parentMap.set(child.id, brainPart.id);
    }
  }

  // Return the parent map.
  return parentMap;
};

/**
 * Defines the root parent id for the dashboard brain part.
 */
export const dashboardBrainPartsDefineRootParentId = (
  brainPartId: DashboardBrainPartSecondaryLevel['id'],
  brainPartsParentMap: DashboardBrainPartsParentMap
): DashboardBrainPartSecondaryLevel['id'] => {
  let currentBrainPartId = brainPartId;
  let nearestParentBrainPartId = brainPartsParentMap.get(currentBrainPartId);

  // Iterate over the parent map until the root parent is found.
  while (nearestParentBrainPartId) {
    currentBrainPartId = nearestParentBrainPartId;
    nearestParentBrainPartId = brainPartsParentMap.get(nearestParentBrainPartId);
  }

  return currentBrainPartId;
};

/**
 * Form a flat list map for the dashboard brain parts.
 * The result is an array of relations between the brain part id and the brain part.
 */
export const dashboardBrainPartsFormFlatListMap = (brainParts: DashboardBrainPartSecondaryLevel[]): DashboardBrainPartsFlatListMap => {
  // Initialize stack with the root brain parts.
  const stackToProceed = [...brainParts];

  // Initialize a parent map as an JS Map.
  const flatListMap: Map<DashboardBrainPartSecondaryLevel['id'], DashboardBrainPartSecondaryLevel> = new Map();

  // Iterate over the stack.
  while (stackToProceed.length > 0) {
    // Extract the last element from the stack.
    const brainPart = stackToProceed.pop();

    // Add the brain part to the flat list map.
    flatListMap.set(brainPart.id, brainPart);

    // Push the child regions of the extracted element to the stack.
    for (const child of brainPart.childRegions) {
      stackToProceed.push(child);
    }
  }

  // Return the parent map.
  return flatListMap;
};

/**
 * Defines the nearest brain part with the chart.
 */
export const dashboardBrainPartsDefineNearestPartWithChart = (
  brainPart: DashboardBrainPartSecondaryLevel,
  brainPartsParentMap: DashboardBrainPartsParentMap,
  brainPartsFlatListMap: DashboardBrainPartsFlatListMap
): DashboardBrainPartSecondaryLevel => {
  let currentBrainPart = brainPart;

  // Iterate over the parent brain parts until the brain part with the chart is found.
  while (currentBrainPart?.shouldDisplayParentChart) {
    const parentBrainPartId = brainPartsParentMap.get(currentBrainPart.id);
    currentBrainPart = brainPartsFlatListMap.get(parentBrainPartId);
  }

  // If all the parent brain parts have no chart, throw an error.
  if (!currentBrainPart) {
    throw new Error(
      `All brain parts in the node are requesting to show the parent chart, so no chart could be displayed.
       To hide the chart, use 'shouldHideRelatedChart' property.`
    );
  }

  // Return the brain part with the chart.
  return currentBrainPart;
};

/**
 * Merge two brain regions data to accumulate all necessary data for the chart.
 * Main level data contains necessary information about the view and chart types.
 * Secondary level data contains actually selected brain part (for cases when sublevel brain part is selected).
 */
export const mergeBrainPartsData = (
  brainPartMainLevel: DashboardBrainPart,
  brainPartToShow: DashboardBrainPartSecondaryLevel
): DashboardBrainPart => {
  // When the main level brain part is selected, return it.
  if (brainPartMainLevel?.id === brainPartToShow?.id) {
    return brainPartMainLevel;
  }

  // When the sublevel brain part is selected, merge its data with the main level brain part data.
  return {
    ...brainPartToShow,
    viewType: brainPartMainLevel.viewType,
    chartType: brainPartMainLevel.chartType,
  };
};

export interface DashboardBrainPartsDataForTypicalRangeComparison {
  /** The brain part MRI label data. */
  mriLabelData: DashboardBrainPartMriLabel;

  /**
   * The name which should be used for extracting the related data.
   */
  nameForComparison: string;

  /** Describes the scan type that needed to be approved to display the current brain part region. */
  requiredScanType: SubmissionScanImageType;

  /** The brain part calculated data (Key Layer). */
  brainPartCalculatedData: DashboardBrainPartCalculatedData;

  /** The white matter data for each subregion. */
  wmData: DashboardChartWhiteMatterDataRelation;

  /** The pvs data for each subregion. */
  pvsData: DashboardChartPvsDataRelation;
}

/**
 * Defines the hex color to apply for the brain part.
 */
export const dashboardBrainPartsUtilsHexColorToApply = (dataForComparison: DashboardBrainPartsDataForTypicalRangeComparison): HexColor => {
  // Define the color to apply based on the color type.
  switch (dataForComparison.mriLabelData.colorType) {
    // Typical atypical comparison:
    // For the common condition, the lower zone is placed below the typical zone.
    // For the inverted condition, the lower zone is placed above the typical zone.
    case DashboardMriLabelColorType.TypicalAtypical:
      const comparisonResult = dashboardBrainPartsUtilsIsTypicalRange(dataForComparison);

      // Define the color to apply.
      return comparisonResult === DashboardChartDataUtilsComparisonResult.AtypicalBad
        ? dataForComparison.mriLabelData.colorAtypical
        : dataForComparison.mriLabelData.colorTypical;

    // Pvs derivative: define the score group and compare it with the typical range.
    case DashboardMriLabelColorType.ScoringPvs:
      const pvsData = dataForComparison.pvsData?.get(dataForComparison.nameForComparison);
      const pvsScoreGroup = dashboardChartDataDefinePvsScoreGroup(pvsData?.score);

      return DashboardChartDataPvsGroupColors[pvsScoreGroup];

    // Wmh derivative: define the score group and compare it with the typical range.
    case DashboardMriLabelColorType.ScoringWmh:
      const wmData = dataForComparison.wmData?.get(dataForComparison.nameForComparison);
      const wmScoreGroup = dashboardChartDataDefineWmScoreGroup(wmData?.score);

      return DashboardChartDataPvsGroupColors[wmScoreGroup];

    // Always set the typical color for the constant color type.
    case DashboardMriLabelColorType.Constant:
      return dataForComparison.mriLabelData.colorTypical;
  }

  // Return the typical color by default.
  return dataForComparison.mriLabelData.colorTypical;
};

/**
 * Defines the user's zone by comparing the user's percentage data with the typical range points.
 */
export const dashboardBrainPartsUtilsIsTypicalRange = (
  dataForComparison: DashboardBrainPartsDataForTypicalRangeComparison
): DashboardChartDataUtilsComparisonResult => {
  switch (dataForComparison.mriLabelData.atypicalCondition) {
    // For the common condition, the lower zone is placed below the typical zone.
    // 0-4% considered as a 'Low' value, 5-95% considered as a 'Typical' value, 96-100% considered as a 'High' value.
    case DashboardBrainPartMriLabelAtypicalCondition.CommonCondition: {
      if (dataForComparison.brainPartCalculatedData?.percentOfUsersWithLowerRatio < firstTypicalZoneAgePercentagePoint) {
        return DashboardChartDataUtilsComparisonResult.AtypicalBad;
      } else if (dataForComparison.brainPartCalculatedData?.percentOfUsersWithLowerRatio > secondTypicalZoneAgePercentagePoint) {
        return DashboardChartDataUtilsComparisonResult.AtypicalGood;
      } else {
        return DashboardChartDataUtilsComparisonResult.Typical;
      }
    }

    case DashboardBrainPartMriLabelAtypicalCondition.InvertedCondition: {
      // For the inverted condition, the lower zone is placed above the typical zone.
      // 0-4% considered as a 'High' value, 5-95% considered as a 'Typical' value, 96-100% considered as a 'Low' value.
      // Take note: the `secondTypicalZoneAgePercentagePoint` is used as a first point, because the legend is inverted.
      if (dataForComparison.brainPartCalculatedData?.percentOfUsersWithLowerRatio > secondTypicalZoneAgePercentagePoint) {
        return DashboardChartDataUtilsComparisonResult.AtypicalBad;
      } else if (dataForComparison.brainPartCalculatedData?.percentOfUsersWithLowerRatio < firstTypicalZoneAgePercentagePoint) {
        return DashboardChartDataUtilsComparisonResult.AtypicalGood;
      } else {
        return DashboardChartDataUtilsComparisonResult.Typical;
      }
    }
  }
  return DashboardChartDataUtilsComparisonResult.Typical;
};

/**
 * Defines loading priority for the related surfaces.
 * It is necessary to load and display the outer surfaces first.
 * So the user will see almost complete brain part during the inner surface loading.
 */
export const dashboardBrainPartsDefineLoadingPriority = (brainPartsFlatList: DashboardBrainPartsFlatListMap): number[][] => {
  // Initialize the priority store variable.
  const priorities = {
    1: new Set<number>(),
    2: new Set<number>(),
    3: new Set<number>(),
  };

  // Iterate over the brain parts flat list.
  for (const brainPart of brainPartsFlatList.values()) {
    // Add the surface ids to the corresponding priority.
    for (const mriLabelData of brainPart.relatedSurfaceRegionData) {
      priorities[brainPart.loadingPriority]?.add(mriLabelData.surfaceLabel);
    }
  }

  // Return the priorities.
  return [priorities[1], priorities[2], priorities[3]].map(priority => Array.from(priority));
};

/**
 * Extracts the scan ids from the brain part.
 */
export const dashboardBrainPartsExtractLabelScanIds = (brainPart: DashboardBrainPartSecondaryLevel): number[] => {
  const scanLabelsIds: number[] = [];
  if (!brainPart?.relatedSurfaceRegionData) {
    return scanLabelsIds;
  }

  // Extract the scan ids from the related mri region data.
  for (const mriLabel of brainPart.relatedSurfaceRegionData) {
    for (const scanLabel of mriLabel.scanLabels) {
      scanLabelsIds.push(scanLabel);
    }
  }

  return scanLabelsIds;
};
